import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { Product } from '../types';
import { useAuth } from './AuthContext';
import wishlistService from '../services/wishlistService';

export interface WishlistContextType {
  items: Product[];
  addItem: (_product: Product) => void;
  removeItem: (_productId: number) => void;
  isInWishlist: (_productId: number) => boolean;
  clearWishlist: () => void;
  getItemCount: () => number;
  refreshWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | null>(null);

interface WishlistState {
  items: Product[];
}

type WishlistAction =
  | { type: 'ADD_ITEM'; payload: Product }
  | { type: 'REMOVE_ITEM'; payload: number }
  | { type: 'CLEAR_WISHLIST' }
  | { type: 'LOAD_WISHLIST'; payload: Product[] };

const wishlistReducer = (state: WishlistState, action: WishlistAction): WishlistState => {
  switch (action.type) {
    case 'ADD_ITEM':
      // Check if item already exists
      const existingItemIndex = state.items.findIndex(
        item => item.id === action.payload.id
      );

      if (existingItemIndex > -1) {
        // Item already in wishlist, don't add again
        return state;
      }

      // Add new item
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
      };

    case 'CLEAR_WISHLIST':
      return { ...state, items: [] };

    case 'LOAD_WISHLIST':
      return { ...state, items: action.payload };

    default:
      return state;
  }
};

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(wishlistReducer, { items: [] });
  const { user } = useAuth();

  // Refresh wishlist from backend or localStorage
  const refreshWishlist = useCallback(async () => {
    try {
      const items = await wishlistService.getAll();
      dispatch({ type: 'LOAD_WISHLIST', payload: items });
    } catch (error) {
      console.error('Error refreshing wishlist:', error);
    }
  }, []);

  // Load wishlist on mount and when user changes
  useEffect(() => {
    refreshWishlist();
  }, [refreshWishlist]);

  // Sync localStorage with backend when user logs in
  useEffect(() => {
    if (user) {
      wishlistService.syncWithBackend().then(() => {
        refreshWishlist();
      });
    }
  }, [user, refreshWishlist]);

  const addItem = useCallback(async (product: Product) => {
    try {
      await wishlistService.add(product);
      dispatch({ type: 'ADD_ITEM', payload: product });
    } catch (error) {
      console.error('Error adding item to wishlist:', error);
    }
  }, []);

  const removeItem = useCallback(async (productId: number) => {
    try {
      await wishlistService.remove(productId);
      dispatch({ type: 'REMOVE_ITEM', payload: productId });
    } catch (error) {
      console.error('Error removing item from wishlist:', error);
    }
  }, []);

  const isInWishlist = useCallback((productId: number): boolean => {
    return state.items.some(item => item.id === productId);
  }, [state.items]);

  const clearWishlist = useCallback(async () => {
    try {
      await wishlistService.clear();
      dispatch({ type: 'CLEAR_WISHLIST' });
    } catch (error) {
      console.error('Error clearing wishlist:', error);
    }
  }, []);

  const getItemCount = useCallback(() => {
    return state.items.length;
  }, [state.items]);

  const value = useMemo(
    () => ({
      items: state.items,
      addItem,
      removeItem,
      isInWishlist,
      clearWishlist,
      getItemCount,
      refreshWishlist,
    }),
    [state.items, addItem, removeItem, isInWishlist, clearWishlist, getItemCount, refreshWishlist]
  );

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = (): WishlistContextType => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
